import { Alert, Box, Snackbar } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../app/store';
import DashboardsSection from './components/DashboardsSection';
// import CostDataSection from './components/CostDataSection';
import { useMyProgramId } from './hooks/useMyProgramId';
import { getEnabledMenuItems } from '../utilities';
import NoPermissionText from '../library/components/NoPermissionText';

/**
 * Financials Page
 *
 * Displays financial dashboards and summaries using Power BI reports.
 * Organized into two sections: the Power BI dashboards (charts) and the
 * corresponding tabular Cost Data.
 *
 * @component
 */

const Financials = () => {
  const { loggedIn, actionsLoaded, actions } = useSelector(
    (state: RootState) => state.user,
  );
  const { programId, isLoading: programLoading } = useMyProgramId();

  // Gate on the `view-financial-views` action (ATH-1570). The nav item and
  // route are hidden for users without it; if they reach /financials directly,
  // show a role-requirement message instead of the dashboards.
  const financialsEnabled = !!getEnabledMenuItems(actions).financials;

  const [dashboardTab, setDashboardTab] = useState(0);
  // const [costDataTab, setCostDataTab] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarShown, setSnackbarShown] = useState(false);

  useEffect(() => {
    if (!loggedIn || !actionsLoaded) return;
    if (programLoading) return;
    if (snackbarShown) return;

    if (!programId) {
      setSnackbarOpen(true);
      setSnackbarShown(true);
    }
  }, [actionsLoaded, loggedIn, programId, programLoading, snackbarShown]);

  const handleDashboardTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setDashboardTab(newValue);
  };

  // const handleCostDataTabChange = (_event: React.SyntheticEvent, newValue: number) => {
  //   setCostDataTab(newValue);
  // };

  // Wait until actions are loaded before deciding, so authorized users don't
  // see a flash of the message. Fail closed: no `view-financial-views` → no
  // financial data, just the role-requirement message.
  if (actionsLoaded && !financialsEnabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 3,
        }}
      >
        <NoPermissionText text="You do not meet the role requirement to see this." />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        p: 3,
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          Your user is not associated with a program. Contact your program admin.
        </Alert>
      </Snackbar>

      <DashboardsSection
        activeTab={dashboardTab}
        onTabChange={handleDashboardTabChange}
      />

      {/* <CostDataSection
        activeTab={costDataTab}
        onTabChange={handleCostDataTabChange}
      /> */}
    </Box>
  );
};

export default Financials;
